/**
 * PlayingState - Active gameplay state
 * Manages the core gameplay loop: player input, enemy spawning,
 * collision detection, scoring, and wave progression.
 */

import { City } from '../entities/City.js';
import { Battery } from '../entities/Battery.js';
import { Missile } from '../entities/Missile.js';
import { MIRVMissile } from '../entities/MIRVMissile.js';
import { SmartMissile } from '../entities/SmartMissile.js';
import { SatelliteBomber } from '../entities/SatelliteBomber.js';
import { Explosion } from '../entities/Explosion.js';
import { Particle } from '../entities/Particle.js';

export class PlayingState {
  constructor(game) {
    this.game = game;

    // Game data (reset on new game)
    this.score = 0;
    this.wave = 1;
    this.cities = [];
    this.batteries = [];
    this.playerMissiles = [];
    this.enemyMissiles = [];
    this.explosions = [];
    this.particles = [];

    // Wave management
    this.enemiesSpawned = 0;
    this.enemiesThisWave = 0;
    this.spawnTimer = 0;
    this.spawnInterval = 1.5;
    this.waveActive = false;

    // Starfield for background (scrolling + twinkling)
    this.stars = [];
    this._initStars(120);
    this.starfieldTime = 0;

    // Crosshair position
    this.crosshairX = 0;
    this.crosshairY = 0;

    // Bonus city system
    this.bonusCityThreshold = 10000; // Every 10,000 points
    this.lastBonusCityScore = 0; // Track last threshold crossed
    this.bonusCityNotification = null; // { timer, cityIndex }

    // Game over sequence (dramatic ending animation)
    this.gameOverSequence = null;
  }

  _initStars(count) {
    this.stars = [];
    for (let i = 0; i < count; i++) {
      this.stars.push({
        x: Math.random(),
        y: Math.random(),
        size: Math.random() * 1.8 + 0.3,
        baseBrightness: Math.random() * 0.5 + 0.2,
        brightness: 0,
        speed: Math.random() * 0.008 + 0.002, // vertical scroll speed (normalized)
        twinkleSpeed: Math.random() * 3 + 1,
        twinklePhase: Math.random() * Math.PI * 2,
        // Some stars have color tint for variety
        colorTint: Math.random() > 0.8 ? (Math.random() > 0.5 ? 'blue' : 'amber') : 'white',
      });
    }
  }

  enter(data = {}) {
    // Check if returning from wave intro or pause (preserve state)
    if (data._resuming) {
      // If returning during game over sequence, just resume the sequence (don't restart wave)
      if (this.gameOverSequence) {
        this._updateOverlay();
        return;
      }
      // Returning from waveIntro or pause - start the wave
      this._startWaveSpawning();
      this._updateOverlay();
      return;
    }

    // Reset game state for new game
    this.score = 0;
    this.wave = 1;
    this._initCities();
    this._initBatteries();
    this.playerMissiles = [];
    this.enemyMissiles = [];
    this.explosions = [];
    this.particles = [];
    this.enemiesSpawned = 0;
    this.enemiesThisWave = 0;
    this.spawnTimer = 0;
    this.waveActive = false;
    this.lastBonusCityScore = 0;
    this.bonusCityNotification = null;
    this.cityNukeFlash = null;
    this.gameOverSequence = null;

    // Show wave intro first
    this.game.state.setState('waveIntro', { wave: this.wave, playingState: this });
  }

  exit() {
    // Ambient audio is managed by StateManager to persist across gameplay-related states
  }

  /**
   * Handle browser window resize - reposition all game entities proportionally.
   * Cities and batteries use percentage-based positions relative to canvas size.
   * In-flight entities are scaled proportionally from old to new dimensions.
   */
  onResize(oldW, oldH, newW, newH) {
    const scaleX = newW / oldW;
    const scaleY = newH / oldH;

    // Reposition cities using their percentage-based positions
    const cityPercentages = [0.15, 0.25, 0.40, 0.60, 0.75, 0.85];
    const newCityY = newH - 30;
    for (let i = 0; i < this.cities.length; i++) {
      this.cities[i].x = newW * cityPercentages[i];
      this.cities[i].y = newCityY;
    }

    // Reposition batteries using their percentage-based positions
    const batteryPercentages = [0.08, 0.50, 0.92];
    const newBatteryY = newH - 25;
    for (let i = 0; i < this.batteries.length; i++) {
      this.batteries[i].x = newW * batteryPercentages[i];
      this.batteries[i].y = newBatteryY;
    }

    // Scale in-flight player missiles proportionally
    for (const m of this.playerMissiles) {
      m.x *= scaleX;
      m.y *= scaleY;
      m.targetX *= scaleX;
      m.targetY *= scaleY;
      if (m.trail) {
        for (const pt of m.trail) {
          pt.x *= scaleX;
          pt.y *= scaleY;
        }
      }
    }

    // Scale in-flight enemy missiles proportionally
    for (const m of this.enemyMissiles) {
      m.x *= scaleX;
      m.y *= scaleY;
      if (m.targetX !== undefined) m.targetX *= scaleX;
      if (m.targetY !== undefined) m.targetY *= scaleY;
      if (m.trail) {
        for (const pt of m.trail) {
          pt.x *= scaleX;
          pt.y *= scaleY;
        }
      }
    }

    // Scale explosions proportionally
    for (const e of this.explosions) {
      e.x *= scaleX;
      e.y *= scaleY;
    }

    // Scale particles proportionally
    for (const p of this.particles) {
      p.x *= scaleX;
      p.y *= scaleY;
    }

    // Scale crosshair position
    this.crosshairX *= scaleX;
    this.crosshairY *= scaleY;
  }

  _initCities() {
    this.cities = [];
    const w = this.game.width;
    const h = this.game.height;
    const cityY = h - 30;
    const positions = [
      w * 0.15, w * 0.25,
      w * 0.40, w * 0.60,
      w * 0.75, w * 0.85,
    ];
    for (let i = 0; i < 6; i++) {
      this.cities.push(new City(positions[i], cityY));
    }
  }

  _initBatteries() {
    this.batteries = [];
    const w = this.game.width;
    const h = this.game.height;
    const batteryY = h - 25;
    const positions = [w * 0.08, w * 0.50, w * 0.92];
    for (let i = 0; i < 3; i++) {
      this.batteries.push(new Battery(positions[i], batteryY));
    }
  }

  _startWaveSpawning() {
    const wave = this.wave;
    // Scale enemies with wave number
    this.enemiesThisWave = Math.min(10 + (wave - 1) * 2, 30);
    this.enemiesSpawned = 0;
    this.spawnTimer = 0;
    // Spawn interval decreases with difficulty
    this.spawnInterval = Math.max(0.4, 1.5 - (wave - 1) * 0.1);
    this.waveActive = true;

    // Refill battery ammo each wave
    for (const battery of this.batteries) {
      battery.refill();
    }
  }

  _getWaveSpeed() {
    return 60 + (this.wave - 1) * 15;
  }

  update(dt) {
    const input = this.game.input;

    // If game over sequence is active, handle it separately
    if (this.gameOverSequence) {
      this._updateGameOverSequence(dt, input);
      return;
    }

    // Track crosshair position (clamped to canvas bounds)
    const pos = input.getPointerPosition();
    this.crosshairX = Math.max(0, Math.min(pos.x, this.game.width));
    this.crosshairY = Math.max(0, Math.min(pos.y, this.game.height));

    // Escape to pause
    if (input.isKeyJustPressed('Escape')) {
      this.game.state.setState('paused', { playingState: this });
      return;
    }

    // Player fire
    if (input.wasClicked()) {
      this._fireMissile(pos.x, pos.y);
    }

    // Spawn enemy missiles
    if (this.waveActive && this.enemiesSpawned < this.enemiesThisWave) {
      this.spawnTimer += dt;
      if (this.spawnTimer >= this.spawnInterval) {
        this._spawnEnemyMissile();
        this.spawnTimer = 0;
      }
    }

    // Update starfield animation
    this.starfieldTime += dt;
    this._updateStarfield(dt);

    // Update all game entities
    this._updateCities(dt);
    this._updatePlayerMissiles(dt);
    this._updateEnemyMissiles(dt);
    this._updateExplosions(dt);
    this._updateParticles(dt);
    this._checkCollisions();

    // Update bonus city notification timer
    if (this.bonusCityNotification) {
      this.bonusCityNotification.timer += dt;
      if (this.bonusCityNotification.timer >= this.bonusCityNotification.duration) {
        this.bonusCityNotification = null;
      }
    }

    // Update empty click flash timer
    if (this.emptyClickFlash) {
      this.emptyClickFlash.timer += dt;
      if (this.emptyClickFlash.timer >= this.emptyClickFlash.duration) {
        this.emptyClickFlash = null;
      }
    }

    // Update city nuke screen flash timer
    if (this.cityNukeFlash) {
      this.cityNukeFlash.timer += dt;
      if (this.cityNukeFlash.timer >= this.cityNukeFlash.duration) {
        this.cityNukeFlash = null;
      }
    }

    // Check game-ending conditions (these may change state, so check and return)
    if (this._checkGameOver()) return;
    if (this._checkWaveComplete()) return;

    // Update overlay (throttled - every 6 frames for performance)
    this._overlayCounter = (this._overlayCounter || 0) + 1;
    if (this._overlayCounter >= 6) {
      this._overlayCounter = 0;
      this._updateOverlay();
    }
  }

  _fireMissile(targetX, targetY) {
    // Find nearest battery with ammo
    let nearest = null;
    let minDist = Infinity;

    for (const battery of this.batteries) {
      if (battery.ammo > 0 && battery.alive) {
        const dist = Math.abs(battery.x - targetX);
        if (dist < minDist) {
          minDist = dist;
          nearest = battery;
        }
      }
    }

    if (nearest) {
      nearest.fire();
      const missile = new Missile({
        x: nearest.x,
        y: nearest.y - 12,
        targetX,
        targetY,
        speed: 400,
        color: '#00ffff',
        trailColor: '#0088ff',
      });
      this.playerMissiles.push(missile);

      // Play launch sound
      if (this.game.audio) {
        this.game.audio.play('launch');
      }
    } else {
      // No battery available - provide empty click feedback
      // Visual: brief red flash on crosshair
      this.emptyClickFlash = { timer: 0, duration: 0.3 };

      // Audio: play empty click sound
      if (this.game.audio) {
        this.game.audio.play('empty_click');
      }
    }
  }

  _spawnEnemyMissile() {
    const w = this.game.width;
    const h = this.game.height;
    const wave = this.wave;

    // Determine enemy type based on wave
    const enemyType = this._selectEnemyType(wave);

    // Handle satellite spawning separately (horizontal movement)
    if (enemyType === 'satellite') {
      this._spawnSatellite();
      this.enemiesSpawned++;
      return;
    }

    // Random start position at top
    const startX = Math.random() * w * 0.8 + w * 0.1;
    const startY = -10;

    // Pick a random target: either a city or battery
    const targets = [];
    for (const city of this.cities) {
      if (city.alive) targets.push({ x: city.x, y: city.y });
    }
    for (const battery of this.batteries) {
      if (battery.alive) targets.push({ x: battery.x, y: battery.y });
    }

    if (targets.length === 0) return;

    const target = targets[Math.floor(Math.random() * targets.length)];
    const baseSpeed = this._getWaveSpeed();

    // Varied speed: multiply by 0.6-1.4 for clearly different fast/slow missiles
    const speedMultiplier = 0.6 + Math.random() * 0.8;
    const missileSpeed = baseSpeed * speedMultiplier;

    let missile;
    if (enemyType === 'mirv') {
      missile = new MIRVMissile({
        x: startX,
        y: startY,
        targetX: target.x,
        targetY: target.y,
        speed: missileSpeed * 0.85, // MIRVs slightly slower
        gameWidth: w,
        gameHeight: h,
      });
    } else if (enemyType === 'smart') {
      missile = new SmartMissile({
        x: startX,
        y: startY,
        targetX: target.x,
        targetY: target.y,
        speed: missileSpeed * 0.9, // Smart missiles slightly slower
      });
    } else {
      missile = new Missile({
        x: startX,
        y: startY,
        targetX: target.x,
        targetY: target.y,
        speed: missileSpeed,
        color: '#ff3333',
        trailColor: '#ff6600',
      });
    }

    this.enemyMissiles.push(missile);
    this.enemiesSpawned++;
  }

  /**
   * Select enemy type based on wave number.
   * Wave 1-3: basic only
   * Wave 4-5: basic + MIRV
   * Wave 6-8: basic + MIRV + smart
   * Wave 9+: basic + MIRV + smart + satellite
   */
  _selectEnemyType(wave) {
    const roll = Math.random();

    if (wave >= 9) {
      // 20% basic, 30% MIRV, 30% smart, 20% satellite
      if (roll < 0.20) return 'basic';
      if (roll < 0.50) return 'mirv';
      if (roll < 0.80) return 'smart';
      return 'satellite';
    } else if (wave >= 6) {
      // 30% basic, 40% MIRV, 30% smart
      if (roll < 0.30) return 'basic';
      if (roll < 0.70) return 'mirv';
      return 'smart';
    } else if (wave >= 4) {
      // 60% basic, 40% MIRV
      if (roll < 0.60) return 'basic';
      return 'mirv';
    }
    return 'basic';
  }

  /**
   * Spawn a satellite/bomber that crosses the screen horizontally.
   */
  _spawnSatellite() {
    const w = this.game.width;
    const h = this.game.height;
    const direction = Math.random() > 0.5 ? 1 : -1;
    const startX = direction > 0 ? -30 : w + 30;
    const altitude = h * 0.1 + Math.random() * h * 0.15; // 10-25% from top

    const satellite = new SatelliteBomber({
      startX,
      y: altitude,
      speed: 80 + this.wave * 5,
      direction,
      gameWidth: w,
      gameHeight: h,
      dropInterval: Math.max(0.8, 2.0 - this.wave * 0.1),
    });
    this.enemyMissiles.push(satellite);
  }

  _updateCities(dt) {
    for (const city of this.cities) {
      city.update(dt);
    }
  }

  _updatePlayerMissiles(dt) {
    for (let i = this.playerMissiles.length - 1; i >= 0; i--) {
      const m = this.playerMissiles[i];
      m.update(dt);

      if (!m.alive) {
        // Create explosion at target
        const explosion = new Explosion(m.targetX, m.targetY, 40);
        this.explosions.push(explosion);
        this._spawnExplosionParticles(m.targetX, m.targetY, '#00ffff', 40);

        // Play explosion sound
        if (this.game.audio) {
          this.game.audio.play('explosion_medium');
        }

        this.playerMissiles.splice(i, 1);
      }
    }
  }

  _updateEnemyMissiles(dt) {
    const newMissiles = []; // Buffer for child missiles spawned this frame

    for (let i = this.enemyMissiles.length - 1; i >= 0; i--) {
      const m = this.enemyMissiles[i];

      // Smart missiles need explosion data for avoidance
      if (m.type === 'smart') {
        m.update(dt, this.explosions);
      } else {
        m.update(dt);
      }

      // Handle MIRV splitting - creates child warheads
      if (m.type === 'mirv' && m.hasSplit && m.childMissiles.length > 0) {
        for (const child of m.childMissiles) {
          newMissiles.push(new Missile({
            x: child.x,
            y: child.y,
            targetX: child.targetX,
            targetY: child.targetY,
            speed: child.speed,
            color: child.color,
            trailColor: child.trailColor,
          }));
        }
        m.childMissiles = [];
        // Create split visual effect
        this._spawnExplosionParticles(m.x, m.y, '#cc44ff');
        if (this.game.audio) {
          this.game.audio.play('mirv_split');
        }
      }

      // Handle satellite bomb drops
      if (m.type === 'satellite' && m.droppedMissiles && m.droppedMissiles.length > 0) {
        for (const drop of m.droppedMissiles) {
          newMissiles.push(new Missile({
            x: drop.x,
            y: drop.y,
            targetX: drop.targetX,
            targetY: drop.targetY,
            speed: drop.speed,
            color: drop.color,
            trailColor: drop.trailColor,
          }));
        }
      }

      if (!m.alive) {
        // Check if it was a satellite going off-screen (no impact) or a missile reaching target
        if (m.type === 'satellite') {
          // Satellites just leave the screen, no impact
          this.enemyMissiles.splice(i, 1);
        } else if (!m.hasSplit) {
          // Normal missile or MIRV child reached target - check what it hit
          this._handleEnemyImpact(m.targetX, m.targetY);
          this.enemyMissiles.splice(i, 1);
        } else {
          // MIRV parent that split - just remove it
          this.enemyMissiles.splice(i, 1);
        }
      }
    }

    // Add all new child missiles
    for (const nm of newMissiles) {
      this.enemyMissiles.push(nm);
    }
  }

  _handleEnemyImpact(x, y) {
    // Check city hits
    for (const city of this.cities) {
      if (city.alive && Math.abs(city.x - x) < 25 && Math.abs(city.y - y) < 25) {
        city.destroy();
        this._spawnExplosionParticles(x, y, '#ff6600');
        // Screen flash effect for dramatic city nuke
        this.cityNukeFlash = { timer: 0, duration: 0.25, alpha: 0.45 };
        if (this.game.audio) {
          this.game.audio.play('city_destroy');
        }
        return;
      }
    }

    // Check battery hits
    for (const battery of this.batteries) {
      if (battery.alive && Math.abs(battery.x - x) < 20 && Math.abs(battery.y - y) < 20) {
        battery.alive = false;
        battery.ammo = 0;
        this._spawnExplosionParticles(x, y, '#ff6600');
        if (this.game.audio) {
          this.game.audio.play('explosion_large');
        }
        return;
      }
    }

    // Ground hit - small explosion
    this._spawnExplosionParticles(x, y, '#ff4400');
    if (this.game.audio) {
      this.game.audio.play('explosion_small');
    }
  }

  _spawnExplosionParticles(x, y, color, explosionRadius = 40) {
    // Scale particle count and spread based on explosion size
    const sizeFactor = explosionRadius / 40; // 1.0 for standard, 0.625 for small (25px)
    const baseCount = 10 + Math.floor(Math.random() * 8);
    const count = Math.floor(baseCount * sizeFactor);

    // --- Spark particles: fast, small, bright ---
    const sparkCount = Math.floor(count * 0.5);
    for (let i = 0; i < sparkCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = (Math.random() * 120 + 60) * sizeFactor;
      // Vary spark colors: mix of base color, white, and orange
      const sparkColors = [color, '#ffffff', '#ffdd66', '#ffaa00'];
      const sparkColor = sparkColors[Math.floor(Math.random() * sparkColors.length)];
      this.particles.push(new Particle(x, y, {
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: Math.random() * 0.5 + 0.2,
        color: sparkColor,
        size: Math.random() * 1.5 + 0.5,
        decay: 1.2,
      }));
    }

    // --- Debris particles: slower, larger, heavier ---
    const debrisCount = Math.floor(count * 0.3);
    for (let i = 0; i < debrisCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = (Math.random() * 50 + 20) * sizeFactor;
      this.particles.push(new Particle(x, y, {
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 15, // slight upward bias
        life: Math.random() * 0.8 + 0.4,
        color: color,
        size: Math.random() * 2.5 + 1.5,
        decay: 0.8,
      }));
    }

    // --- Hot ember particles: very slow, long-lived, glow ---
    const emberCount = Math.floor(count * 0.2);
    for (let i = 0; i < emberCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = (Math.random() * 25 + 10) * sizeFactor;
      const emberColors = ['#ff6600', '#ffaa00', '#ff4400'];
      this.particles.push(new Particle(x, y, {
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 20,
        life: Math.random() * 1.0 + 0.6,
        color: emberColors[Math.floor(Math.random() * emberColors.length)],
        size: Math.random() * 1.8 + 0.8,
        decay: 0.6,
      }));
    }
  }

  _updateExplosions(dt) {
    for (let i = this.explosions.length - 1; i >= 0; i--) {
      this.explosions[i].update(dt);
      if (!this.explosions[i].alive) {
        this.explosions.splice(i, 1);
      }
    }
  }

  _updateParticles(dt) {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      this.particles[i].update(dt);
      if (!this.particles[i].alive) {
        this.particles.splice(i, 1);
      }
    }
  }

  _checkCollisions() {
    // Check if explosions destroy enemy missiles
    for (let i = this.enemyMissiles.length - 1; i >= 0; i--) {
      const m = this.enemyMissiles[i];
      for (const explosion of this.explosions) {
        if (explosion.containsPoint(m.x, m.y)) {
          // Award points based on enemy type
          const points = this._getPointsForType(m.type);
          const scoreBefore = this.score;
          this.score += points;

          // Check for bonus city award
          this._checkBonusCity(scoreBefore, this.score);

          // Handle MIRV: prevent child spawning if destroyed before split
          if (m.type === 'mirv' && !m.hasSplit) {
            m.hasSplit = true;
            m.childMissiles = [];
          }

          m.alive = false;

          // Chain reaction - create smaller explosion (larger for satellites)
          const chainRadius = m.type === 'satellite' ? 35 : 25;
          const smallExplosion = new Explosion(m.x, m.y, chainRadius);
          this.explosions.push(smallExplosion);

          const particleColor = m.type === 'mirv' ? '#cc44ff' :
                                m.type === 'smart' ? '#ffaa33' :
                                m.type === 'satellite' ? '#44ff88' : '#ffaa00';
          this._spawnExplosionParticles(m.x, m.y, particleColor);

          if (this.game.audio) {
            this.game.audio.play(m.type === 'satellite' ? 'explosion_large' : 'explosion_small');
          }

          this.enemyMissiles.splice(i, 1);
          break;
        }
      }
    }
  }

  /**
   * Get point value based on enemy missile type.
   */
  _getPointsForType(type) {
    switch (type) {
      case 'mirv': return 50;
      case 'smart': return 100;
      case 'satellite': return 150;
      default: return 25; // basic
    }
  }

  /**
   * Check if score crossing a 10,000-point threshold awards a bonus city.
   * Restores the first destroyed city found (if any).
   * Supports multiple thresholds crossed at once (e.g., score jumps from 9000 to 21000).
   */
  _checkBonusCity(scoreBefore, scoreAfter) {
    const prevThreshold = Math.floor(scoreBefore / this.bonusCityThreshold);
    const newThreshold = Math.floor(scoreAfter / this.bonusCityThreshold);

    if (newThreshold > prevThreshold) {
      // Award one bonus city per threshold crossed
      const thresholdsCrossed = newThreshold - prevThreshold;
      for (let i = 0; i < thresholdsCrossed; i++) {
        const destroyed = this.cities.find(c => !c.alive);
        if (destroyed) {
          destroyed.restore();

          // Create visual notification
          this.bonusCityNotification = {
            timer: 0,
            duration: 3.0,
            cityIndex: this.cities.indexOf(destroyed),
            threshold: (prevThreshold + i + 1) * this.bonusCityThreshold,
          };

          // Spawn celebration particles at the restored city
          this._spawnBonusCityParticles(destroyed.x, destroyed.y);

          // Play wave complete fanfare for bonus city
          if (this.game.audio) {
            this.game.audio.play('wave_complete');
          }
        }
      }
    }
  }

  /**
   * Spawn green/gold celebration particles when a bonus city is awarded.
   */
  _spawnBonusCityParticles(x, y) {
    const colors = ['#00ff88', '#ffdd00', '#00ffff', '#ffffff'];
    for (let i = 0; i < 20; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 100 + 40;
      this.particles.push(new Particle(x, y, {
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 40,
        life: Math.random() * 1.0 + 0.5,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 2.5 + 1.0,
        decay: 0.8,
      }));
    }
  }

  _checkGameOver() {
    const aliveCities = this.cities.filter(c => c.alive).length;
    if (aliveCities === 0) {
      this.waveActive = false;
      if (this.game.audio) {
        this.game.audio.play('game_over');
      }
      // Start the dramatic game over sequence instead of immediately transitioning
      this._startGameOverSequence();
      return true;
    }
    return false;
  }

  /**
   * Initialize the dramatic game over sequence.
   * Phases:
   *   0 (0-2s): "GAME OVER" text appears, last city's mushroom cloud plays
   *   1 (2-5s): Random mushroom clouds erupt along the bottom
   *   2 (5-8s): MEGA mushroom cloud grows (75% screen), massive shockwave + screen shake
   *   3 (8-12s): Fade to black, "GAME OVER" text centered
   *   4 (12s+): Fully faded, waiting for user click/keypress
   */
  _startGameOverSequence() {
    const w = this.game.width;
    const h = this.game.height;

    this.gameOverSequence = {
      timer: 0,
      phase: 0,

      // "GAME OVER" text
      textAlpha: 0,
      textPulse: 0,

      // Mini mushroom clouds (random along bottom)
      miniClouds: [],
      miniSpawnTimer: 0,
      miniSpawnCount: 0,

      // MEGA mushroom cloud
      mega: {
        active: false,
        timer: 0,
        x: w / 2,
        baseY: h - 10,
        flashAlpha: 0,
        shockwaveRadius: 0,
        shockwaveAlpha: 0,
        fireballRadius: 0,
        stemHeight: 0,
        stemWidth: 0,
        capRadius: 0,
        capY: h - 10,
      },

      // Screen shake
      shakeIntensity: 0,
      shakeX: 0,
      shakeY: 0,

      // Fade to black
      fadeAlpha: 0,

      // Ready for user input
      canContinue: false,

      // Flash overlay for mega detonation
      megaFlashAlpha: 0,
    };
  }

  /**
   * Update the game over sequence each frame.
   */
  _updateGameOverSequence(dt, input) {
    const seq = this.gameOverSequence;
    const w = this.game.width;
    const h = this.game.height;
    seq.timer += dt;
    const t = seq.timer;

    // Continue updating background animations (starfield, city smoke, particles, explosions)
    this.starfieldTime += dt;
    this._updateStarfield(dt);
    this._updateCities(dt);
    this._updateExplosions(dt);
    this._updateParticles(dt);
    // Let remaining enemy missiles and player missiles finish their paths
    this._updatePlayerMissiles(dt);
    this._updateEnemyMissiles(dt);

    // Update city nuke flash
    if (this.cityNukeFlash) {
      this.cityNukeFlash.timer += dt;
      if (this.cityNukeFlash.timer >= this.cityNukeFlash.duration) {
        this.cityNukeFlash = null;
      }
    }

    // === Phase 0: "GAME OVER" text fades in (0-2s) ===
    if (t < 2.0) {
      seq.textAlpha = Math.min(1, t / 1.0); // Fade in over 1s
      seq.textPulse = t;
    }

    // === Phase 1: Random mini mushroom clouds along bottom (2-5s) ===
    if (t >= 2.0 && t < 5.0) {
      seq.phase = 1;
      seq.textAlpha = 1;
      seq.textPulse = t;

      // Spawn mini clouds at intervals
      seq.miniSpawnTimer += dt;
      const spawnInterval = 0.15; // Fast spawning for "lots of random clouds"
      if (seq.miniSpawnTimer >= spawnInterval && seq.miniSpawnCount < 25) {
        seq.miniSpawnTimer = 0;
        seq.miniSpawnCount++;

        // Random position along bottom
        const cloudX = Math.random() * w;
        const cloudBaseY = h - 10 + (Math.random() - 0.5) * 10;
        seq.miniClouds.push({
          x: cloudX,
          baseY: cloudBaseY,
          timer: 0,
          scale: 0.4 + Math.random() * 0.6, // Random size
          stemHeight: 0,
          capRadius: 0,
          capY: cloudBaseY,
          fireballRadius: 0,
          flashAlpha: 1.0,
          shockwaveRadius: 0,
          shockwaveAlpha: 1.0,
          maxStemHeight: (40 + Math.random() * 60) * (0.4 + Math.random() * 0.6),
          maxCapRadius: (15 + Math.random() * 25) * (0.4 + Math.random() * 0.6),
          alive: true,
        });

        // Play city_destroy for the first few, then just explosion sounds
        if (this.game.audio) {
          if (seq.miniSpawnCount <= 5) {
            this.game.audio.play('city_destroy');
          } else if (seq.miniSpawnCount % 3 === 0) {
            this.game.audio.play('explosion_large');
          }
        }

        // Add screen shake for each cloud
        seq.shakeIntensity = Math.min(6, seq.shakeIntensity + 1.5);
      }

      // Minor screen shake during this phase
      seq.shakeIntensity = Math.max(seq.shakeIntensity - dt * 3, 2);
    }

    // === Phase 2: MEGA mushroom cloud (5-10s) ===
    if (t >= 5.0 && t < 10.0) {
      seq.phase = 2;
      seq.textPulse = t;

      if (!seq.mega.active) {
        seq.mega.active = true;
        seq.mega.timer = 0;
        seq.mega.x = w / 2;
        seq.mega.baseY = h - 10;
        seq.megaFlashAlpha = 1.0;

        // Play multiple dramatic sounds
        if (this.game.audio) {
          this.game.audio.play('city_destroy');
          this.game.audio.play('explosion_large');
        }

        // Major screen shake burst
        seq.shakeIntensity = 20;
      }

      seq.mega.timer += dt;
      const mt = seq.mega.timer;
      const maxH = h * 0.75; // 75% of screen height

      // Mega flash (0-0.5s)
      if (mt < 0.5) {
        seq.megaFlashAlpha = Math.pow(1.0 - (mt / 0.5), 2);
      } else {
        seq.megaFlashAlpha = 0;
      }

      // Mega shockwave (0-2s) - massive
      if (mt < 2.0) {
        const sw = mt / 2.0;
        seq.mega.shockwaveRadius = sw * Math.max(w, h) * 0.8;
        seq.mega.shockwaveAlpha = (1 - sw) * 0.9;
      } else {
        seq.mega.shockwaveAlpha = 0;
      }

      // Mega fireball (0-1.5s)
      if (mt < 1.5) {
        const ft = mt / 1.5;
        seq.mega.fireballRadius = Math.sin(ft * Math.PI * 0.7) * w * 0.2;
      } else {
        seq.mega.fireballRadius = Math.max(0, seq.mega.fireballRadius - dt * 80);
      }

      // Mega stem (0.2-4s) - grows to 75% screen
      if (mt > 0.2 && mt < 4.0) {
        const stemT = Math.min(1, (mt - 0.2) / 2.5);
        const eased = 1 - Math.pow(1 - stemT, 2);
        seq.mega.stemHeight = eased * maxH * 0.7;
        seq.mega.stemWidth = 30 + eased * w * 0.08;
      }

      // Mega cap (0.5-5s) - billowing massive dome
      if (mt > 0.5) {
        const capT = Math.min(1, (mt - 0.5) / 2.5);
        const eased = 1 - Math.pow(1 - capT, 3);
        seq.mega.capRadius = eased * w * 0.3;
        seq.mega.capY = seq.mega.baseY - seq.mega.stemHeight;
      }

      // Screen shake: intense at start, gradually decreasing
      seq.shakeIntensity = Math.max(3, 20 * Math.pow(0.85, mt * 3));
    }

    // === Phase 3: Fade to black (8-12s) ===
    if (t >= 8.0) {
      seq.phase = 3;
      // Fade starts at t=8, completes by t=12
      const fadeProgress = Math.min(1, (t - 8.0) / 4.0);
      seq.fadeAlpha = fadeProgress;

      // Reduce screen shake during fade
      seq.shakeIntensity = Math.max(0, seq.shakeIntensity - dt * 5);

      // Text stays visible through the fade
      seq.textAlpha = 1;
      seq.textPulse = t;
    }

    // === Phase 4: Ready for input (12s+) ===
    if (t >= 12.0) {
      seq.phase = 4;
      seq.fadeAlpha = 1.0;
      seq.shakeIntensity = 0;
      seq.canContinue = true;
    }

    // Update all mini mushroom clouds
    for (const cloud of seq.miniClouds) {
      if (!cloud.alive) continue;
      cloud.timer += dt;
      const ct = cloud.timer;

      // Flash
      if (ct < 0.2) {
        cloud.flashAlpha = Math.pow(1.0 - (ct / 0.2), 2);
      } else {
        cloud.flashAlpha = 0;
      }

      // Shockwave
      if (ct < 0.5) {
        const sw = ct / 0.5;
        cloud.shockwaveRadius = sw * 60 * cloud.scale;
        cloud.shockwaveAlpha = (1 - sw) * 0.7;
      } else {
        cloud.shockwaveAlpha = 0;
      }

      // Fireball
      if (ct < 0.6) {
        cloud.fireballRadius = Math.sin((ct / 0.6) * Math.PI) * 20 * cloud.scale;
      } else {
        cloud.fireballRadius = Math.max(0, cloud.fireballRadius - dt * 30);
      }

      // Stem
      if (ct > 0.1 && ct < 2.5) {
        const stemT = Math.min(1, (ct - 0.1) / 1.2);
        cloud.stemHeight = (1 - Math.pow(1 - stemT, 2)) * cloud.maxStemHeight;
      }

      // Cap
      if (ct > 0.3 && ct < 3.0) {
        const capT = Math.min(1, (ct - 0.3) / 1.2);
        cloud.capRadius = (1 - Math.pow(1 - capT, 3)) * cloud.maxCapRadius;
        cloud.capY = cloud.baseY - cloud.stemHeight;
      }

      // Mark dead after animation
      if (ct > 4.0) {
        cloud.alive = false;
      }
    }

    // Update screen shake offset
    if (seq.shakeIntensity > 0.1) {
      seq.shakeX = (Math.random() - 0.5) * 2 * seq.shakeIntensity;
      seq.shakeY = (Math.random() - 0.5) * 2 * seq.shakeIntensity;
    } else {
      seq.shakeX = 0;
      seq.shakeY = 0;
    }

    // Check for user input to continue (only after fade complete)
    if (seq.canContinue) {
      if (input.wasClicked() || input.isKeyJustPressed('Enter') ||
          input.isKeyJustPressed('Space') || input.isKeyJustPressed('Escape')) {
        // Transition to the actual game over screen
        this.gameOverSequence = null;
        this.game.state.setState('gameOver', { score: this.score, wave: this.wave });
        return;
      }
    }

    // Update overlay (throttled)
    this._overlayCounter = (this._overlayCounter || 0) + 1;
    if (this._overlayCounter >= 6) {
      this._overlayCounter = 0;
      this._updateOverlay();
    }
  }

  _checkWaveComplete() {
    if (!this.waveActive) return false;

    // Wave is complete when all enemies spawned and none remain
    if (this.enemiesSpawned >= this.enemiesThisWave &&
        this.enemyMissiles.length === 0 &&
        this.explosions.length === 0) {
      this.waveActive = false;

      // Play wave complete fanfare
      if (this.game.audio) {
        this.game.audio.play('wave_complete');
      }

      // Clear persistent smoke on destroyed cities at wave end
      for (const city of this.cities) {
        city.clearSmoke();
      }

      // Calculate bonuses
      const survivingCities = this.cities.filter(c => c.alive).length;
      const remainingAmmo = this.batteries.reduce((sum, b) => sum + b.ammo, 0);
      const cityBonus = survivingCities * 100 * this.wave;
      const ammoBonus = remainingAmmo * 5 * this.wave;

      this.game.state.setState('waveEnd', {
        wave: this.wave,
        cityBonus,
        ammoBonus,
        survivingCities,
        remainingAmmo,
        playingState: this,
      });
      return true;
    }
    return false;
  }

  /**
   * Called by WaveEndScreen to advance the wave.
   */
  advanceWave(bonusScore) {
    const scoreBefore = this.score;
    this.score += bonusScore;
    this.wave++;

    // Check for bonus cities (every 10,000 points)
    this._checkBonusCity(scoreBefore, this.score);

    // Show wave intro
    this.game.state.setState('waveIntro', { wave: this.wave, playingState: this });
  }

  /**
   * Update the DOM overlay with gameplay info for Playwright testing
   */
  _updateOverlay() {
    if (!this.game.uiOverlay) return;

    // If game over sequence is active, show game over overlay
    if (this.gameOverSequence) {
      const seq = this.gameOverSequence;
      this.game.uiOverlay.innerHTML = `
        <div data-screen="gameOverSequence" data-phase="${seq.phase}" data-can-continue="${seq.canContinue}">
          <div class="game-over-title">GAME OVER</div>
          <div class="final-score" data-score="${this.score}">Score: ${this.score}</div>
          <div class="final-wave" data-wave="${this.wave}">Wave: ${this.wave}</div>
          <div class="sequence-timer" data-timer="${seq.timer.toFixed(1)}">Timer: ${seq.timer.toFixed(1)}</div>
        </div>
      `;
      return;
    }

    const aliveCities = this.cities.filter(c => c.alive).length;
    const totalAmmo = this.batteries.reduce((sum, b) => sum + b.ammo, 0);
    const aliveBatteries = this.batteries.filter(b => b.alive).length;

    // Per-battery ammo data for testing
    const batteryAmmoData = this.batteries.map((b, i) =>
      `<div class="battery-info" data-battery-index="${i}" data-battery-ammo="${b.ammo}" data-battery-alive="${b.alive}" data-battery-x="${Math.round(b.x)}">Battery ${i}: ${b.ammo}</div>`
    ).join('');

    // Per-city data for testing
    const cityData = this.cities.map((c, i) =>
      `<div class="city-info" data-city-index="${i}" data-city-alive="${c.alive}" data-city-x="${Math.round(c.x)}" data-city-y="${Math.round(c.y)}">City ${i}: ${c.alive ? 'alive' : 'destroyed'}</div>`
    ).join('');

    const bonusCityInfo = this.bonusCityNotification
      ? `<div class="bonus-city-notification" data-bonus-city="true" data-bonus-threshold="${this.bonusCityNotification.threshold}">BONUS CITY at ${this.bonusCityNotification.threshold}</div>`
      : '';

    this.game.uiOverlay.innerHTML = `
      <div data-screen="playing">
        <div class="hud-score" data-score="${this.score}">Score: ${this.score}</div>
        <div class="hud-wave" data-wave="${this.wave}">Wave: ${this.wave}</div>
        <div class="hud-cities" data-cities="${aliveCities}">Cities: ${aliveCities}</div>
        <div class="hud-ammo" data-ammo="${totalAmmo}">Ammo: ${totalAmmo}</div>
        <div class="hud-batteries" data-count="${aliveBatteries}">Batteries: ${aliveBatteries}</div>
        <div class="hud-enemies" data-count="${this.enemyMissiles.length}" data-mirv-count="${this.enemyMissiles.filter(m => m.type === 'mirv').length}" data-smart-count="${this.enemyMissiles.filter(m => m.type === 'smart').length}" data-satellite-count="${this.enemyMissiles.filter(m => m.type === 'satellite').length}" data-basic-count="${this.enemyMissiles.filter(m => !m.type || m.type === 'basic').length}">Enemies: ${this.enemyMissiles.length}</div>
        <div class="crosshair-info" data-crosshair-x="${Math.round(this.crosshairX)}" data-crosshair-y="${Math.round(this.crosshairY)}">Crosshair: ${Math.round(this.crosshairX)},${Math.round(this.crosshairY)}</div>
        ${batteryAmmoData}
        ${cityData}
        ${bonusCityInfo}
      </div>
    `;
  }

  render(ctx) {
    const w = this.game.width;
    const h = this.game.height;

    // Apply screen shake for game over sequence (affects all gameplay rendering)
    const seq = this.gameOverSequence;
    if (seq && seq.shakeIntensity > 0.1) {
      ctx.save();
      ctx.translate(seq.shakeX, seq.shakeY);
    }

    // Background - wave-shifting gradient sky
    this._renderBackground(ctx, w, h);

    // Starfield (scrolling + twinkling)
    this._renderStarfield(ctx, w, h);

    // Ground line
    ctx.strokeStyle = '#1a1a4e';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, h - 10);
    ctx.lineTo(w, h - 10);
    ctx.stroke();

    // Render entities (back to front order)
    // Enemy missiles
    for (const m of this.enemyMissiles) {
      m.render(ctx);
    }

    // Player missiles
    for (const m of this.playerMissiles) {
      m.render(ctx);
    }

    // Explosions
    for (const e of this.explosions) {
      e.render(ctx);
    }

    // Particles (batch render for performance - no per-particle shadowBlur)
    Particle.renderBatch(ctx, this.particles);

    // Cities
    for (const city of this.cities) {
      city.render(ctx);
    }

    // Batteries (render all - alive ones show turret, destroyed show rubble)
    for (const battery of this.batteries) {
      battery.render(ctx, w, h);
    }

    // Restore screen shake transform before HUD/UI elements
    if (seq && seq.shakeIntensity > 0.1) {
      ctx.restore();
    }

    // Crosshair cursor (only when not in game over sequence)
    if (!this.gameOverSequence) {
      this._renderCrosshair(ctx);
    }

    // HUD (only when not in game over sequence)
    if (!this.gameOverSequence) {
      this._renderHUD(ctx, w, h);
    }

    // Bonus city notification
    if (this.bonusCityNotification) {
      this._renderBonusCityNotification(ctx, w, h);
    }

    // City nuke screen flash overlay (dramatic white flash on city destruction)
    if (this.cityNukeFlash) {
      const flashT = this.cityNukeFlash.timer / this.cityNukeFlash.duration;
      const flashAlpha = this.cityNukeFlash.alpha * Math.pow(1 - flashT, 2);
      if (flashAlpha > 0.01) {
        ctx.save();
        ctx.fillStyle = `rgba(255, 240, 200, ${flashAlpha})`;
        ctx.fillRect(0, 0, w, h);
        ctx.restore();
      }
    }

    // Game over sequence overlays
    if (this.gameOverSequence) {
      this._renderGameOverSequence(ctx, w, h);
    }
  }

  /**
   * Render the dramatic game over sequence overlays.
   * Draws mini mushroom clouds, mega mushroom cloud, screen shake,
   * "GAME OVER" text, and fade to black.
   */
  _renderGameOverSequence(ctx, w, h) {
    const seq = this.gameOverSequence;

    // Apply screen shake transform
    if (seq.shakeIntensity > 0.1) {
      ctx.save();
      ctx.translate(seq.shakeX, seq.shakeY);
    }

    // --- Mini mushroom clouds ---
    for (const cloud of seq.miniClouds) {
      if (!cloud.alive) continue;
      this._renderMiniCloud(ctx, cloud);
    }

    // --- MEGA mushroom cloud ---
    if (seq.mega.active) {
      this._renderMegaCloud(ctx, seq.mega, w, h);
    }

    // Restore screen shake transform (before drawing HUD overlays)
    if (seq.shakeIntensity > 0.1) {
      ctx.restore();
    }

    // --- Mega flash overlay ---
    if (seq.megaFlashAlpha > 0.01) {
      ctx.save();
      ctx.fillStyle = `rgba(255, 240, 200, ${seq.megaFlashAlpha * 0.7})`;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }

    // --- Fade to black overlay ---
    if (seq.fadeAlpha > 0.01) {
      ctx.save();
      ctx.fillStyle = `rgba(0, 0, 0, ${seq.fadeAlpha})`;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }

    // --- "GAME OVER" text ---
    if (seq.textAlpha > 0.01) {
      ctx.save();
      ctx.globalAlpha = seq.textAlpha;
      const glowPulse = 15 + Math.sin(seq.textPulse * 2) * 10;
      const fontSize = Math.max(40, w * 0.07);
      this.game.renderer.drawGlowText('GAME OVER', w / 2, h * 0.4, {
        font: `${fontSize}px "Share Tech Mono", monospace`,
        color: '#ff3333',
        glowColor: '#ff0000',
        glowSize: glowPulse,
      });

      // "Click to continue" prompt (only when ready)
      if (seq.canContinue) {
        const promptAlpha = 0.5 + 0.5 * Math.sin(seq.textPulse * 4);
        ctx.globalAlpha = promptAlpha;
        const promptSize = Math.max(14, w * 0.02);
        this.game.renderer.drawGlowText('CLICK OR PRESS ANY KEY', w / 2, h * 0.55, {
          font: `${promptSize}px "Share Tech Mono", monospace`,
          color: '#aabbcc',
          glowColor: '#445566',
          glowSize: 3,
        });
      }
      ctx.restore();
    }
  }

  /**
   * Render a mini mushroom cloud at the given position.
   */
  _renderMiniCloud(ctx, cloud) {
    ctx.save();

    // Flash
    if (cloud.flashAlpha > 0.01) {
      const flashR = 30 * cloud.scale + (1 - cloud.flashAlpha) * 20;
      const flashGrad = ctx.createRadialGradient(
        cloud.x, cloud.baseY - 8, 0,
        cloud.x, cloud.baseY - 8, flashR
      );
      flashGrad.addColorStop(0, `rgba(255, 255, 255, ${cloud.flashAlpha})`);
      flashGrad.addColorStop(0.3, `rgba(255, 240, 150, ${cloud.flashAlpha * 0.8})`);
      flashGrad.addColorStop(0.7, `rgba(255, 150, 50, ${cloud.flashAlpha * 0.4})`);
      flashGrad.addColorStop(1, 'rgba(255, 100, 30, 0)');
      ctx.fillStyle = flashGrad;
      ctx.beginPath();
      ctx.arc(cloud.x, cloud.baseY - 8, flashR, 0, Math.PI * 2);
      ctx.fill();
    }

    // Shockwave ring
    if (cloud.shockwaveAlpha > 0.01 && cloud.shockwaveRadius > 0) {
      ctx.strokeStyle = `rgba(255, 220, 120, ${cloud.shockwaveAlpha})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cloud.x, cloud.baseY - 8, cloud.shockwaveRadius, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Fireball
    if (cloud.fireballRadius > 2) {
      const fbGrad = ctx.createRadialGradient(
        cloud.x, cloud.baseY - 6, 0,
        cloud.x, cloud.baseY - 6, cloud.fireballRadius
      );
      fbGrad.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
      fbGrad.addColorStop(0.3, 'rgba(255, 210, 80, 0.7)');
      fbGrad.addColorStop(0.6, 'rgba(255, 120, 20, 0.5)');
      fbGrad.addColorStop(1, 'rgba(180, 50, 0, 0)');
      ctx.fillStyle = fbGrad;
      ctx.beginPath();
      ctx.arc(cloud.x, cloud.baseY - 6, cloud.fireballRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    // Stem
    if (cloud.stemHeight > 3) {
      const fadeT = Math.min(1, Math.max(0, (4.0 - cloud.timer) / 1.5));
      if (fadeT > 0.01) {
        const stemBaseW = 6 * cloud.scale;
        const stemTopW = 4 * cloud.scale;
        const stemBot = cloud.baseY - 6;
        const stemTop = cloud.baseY - cloud.stemHeight;

        const stemGrad = ctx.createLinearGradient(cloud.x, stemBot, cloud.x, stemTop);
        stemGrad.addColorStop(0, `rgba(255, 160, 60, ${0.7 * fadeT})`);
        stemGrad.addColorStop(0.4, `rgba(200, 120, 50, ${0.55 * fadeT})`);
        stemGrad.addColorStop(1, `rgba(140, 100, 80, ${0.4 * fadeT})`);

        ctx.fillStyle = stemGrad;
        ctx.beginPath();
        ctx.moveTo(cloud.x - stemBaseW, stemBot);
        ctx.quadraticCurveTo(cloud.x - stemTopW * 0.7, (stemBot + stemTop) / 2, cloud.x - stemTopW, stemTop);
        ctx.lineTo(cloud.x + stemTopW, stemTop);
        ctx.quadraticCurveTo(cloud.x + stemTopW * 0.7, (stemBot + stemTop) / 2, cloud.x + stemBaseW, stemBot);
        ctx.closePath();
        ctx.fill();
      }
    }

    // Cap
    if (cloud.capRadius > 3) {
      const fadeT = Math.min(1, Math.max(0, (4.0 - cloud.timer) / 1.5));
      if (fadeT > 0.01) {
        const rx = cloud.capRadius * 1.3;
        const ry = cloud.capRadius * 0.8;
        const wobble = Math.sin(cloud.timer * 5) * 1.2;

        const capGrad = ctx.createRadialGradient(
          cloud.x, cloud.capY, ry * 0.1,
          cloud.x, cloud.capY, Math.max(rx, ry)
        );
        capGrad.addColorStop(0, `rgba(255, 220, 130, ${0.75 * fadeT})`);
        capGrad.addColorStop(0.35, `rgba(220, 150, 70, ${0.6 * fadeT})`);
        capGrad.addColorStop(0.7, `rgba(170, 120, 80, ${0.4 * fadeT})`);
        capGrad.addColorStop(1, 'rgba(120, 90, 70, 0)');

        ctx.fillStyle = capGrad;
        ctx.beginPath();
        ctx.ellipse(cloud.x + wobble, cloud.capY, rx, ry, 0, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  /**
   * Render the MEGA mushroom cloud (fills ~75% of screen).
   */
  _renderMegaCloud(ctx, mega, w, h) {
    const mt = mega.timer;
    ctx.save();

    // Mega shockwave - covers entire screen
    if (mega.shockwaveAlpha > 0.01 && mega.shockwaveRadius > 0) {
      const ringWidth = Math.max(3, (1 - mega.shockwaveRadius / (Math.max(w, h) * 0.8)) * 10 + 2);
      ctx.strokeStyle = `rgba(255, 200, 100, ${mega.shockwaveAlpha})`;
      ctx.shadowColor = '#ff6600';
      ctx.shadowBlur = 20 * mega.shockwaveAlpha;
      ctx.lineWidth = ringWidth;
      ctx.beginPath();
      ctx.arc(mega.x, mega.baseY, mega.shockwaveRadius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    // Mega fireball - huge ground-level fireball
    if (mega.fireballRadius > 5) {
      const fbGrad = ctx.createRadialGradient(
        mega.x, mega.baseY - 10, 0,
        mega.x, mega.baseY - 10, mega.fireballRadius
      );
      fbGrad.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
      fbGrad.addColorStop(0.15, 'rgba(255, 240, 150, 0.9)');
      fbGrad.addColorStop(0.35, 'rgba(255, 180, 50, 0.7)');
      fbGrad.addColorStop(0.6, 'rgba(255, 100, 20, 0.4)');
      fbGrad.addColorStop(0.85, 'rgba(200, 50, 0, 0.15)');
      fbGrad.addColorStop(1, 'rgba(150, 30, 0, 0)');
      ctx.fillStyle = fbGrad;
      ctx.beginPath();
      ctx.arc(mega.x, mega.baseY - 10, mega.fireballRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    // Mega stem - massive column
    if (mega.stemHeight > 10) {
      const stemFade = Math.min(1, Math.max(0, 1));
      const stemBaseW = mega.stemWidth;
      const stemTopW = mega.stemWidth * 0.6;
      const stemBot = mega.baseY - 8;
      const stemTop = mega.baseY - mega.stemHeight;

      // Outer stem
      const stemGrad = ctx.createLinearGradient(mega.x, stemBot, mega.x, stemTop);
      stemGrad.addColorStop(0, `rgba(255, 160, 60, ${0.8 * stemFade})`);
      stemGrad.addColorStop(0.2, `rgba(240, 130, 50, ${0.7 * stemFade})`);
      stemGrad.addColorStop(0.5, `rgba(200, 110, 60, ${0.6 * stemFade})`);
      stemGrad.addColorStop(0.8, `rgba(160, 100, 80, ${0.5 * stemFade})`);
      stemGrad.addColorStop(1, `rgba(130, 100, 85, ${0.45 * stemFade})`);

      ctx.fillStyle = stemGrad;
      ctx.beginPath();
      ctx.moveTo(mega.x - stemBaseW, stemBot);
      ctx.quadraticCurveTo(mega.x - stemTopW * 0.8, (stemBot + stemTop) / 2, mega.x - stemTopW, stemTop);
      ctx.lineTo(mega.x + stemTopW, stemTop);
      ctx.quadraticCurveTo(mega.x + stemTopW * 0.8, (stemBot + stemTop) / 2, mega.x + stemBaseW, stemBot);
      ctx.closePath();
      ctx.fill();

      // Inner hot core
      const coreGrad = ctx.createLinearGradient(mega.x, stemBot, mega.x, stemTop);
      coreGrad.addColorStop(0, `rgba(255, 230, 150, 0.5)`);
      coreGrad.addColorStop(0.3, `rgba(255, 200, 100, 0.3)`);
      coreGrad.addColorStop(0.7, `rgba(255, 150, 80, 0.15)`);
      coreGrad.addColorStop(1, 'rgba(200, 130, 80, 0)');
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.moveTo(mega.x - stemBaseW * 0.35, stemBot);
      ctx.lineTo(mega.x - stemTopW * 0.25, stemTop + (stemBot - stemTop) * 0.25);
      ctx.lineTo(mega.x + stemTopW * 0.25, stemTop + (stemBot - stemTop) * 0.25);
      ctx.lineTo(mega.x + stemBaseW * 0.35, stemBot);
      ctx.closePath();
      ctx.fill();
    }

    // Mega cap - billowing massive dome
    if (mega.capRadius > 10) {
      const rx = mega.capRadius * 1.3;
      const ry = mega.capRadius * 0.85;
      const wobble = Math.sin(mt * 3) * 3;

      // Outer cap
      const capGrad = ctx.createRadialGradient(
        mega.x, mega.capY - 5, ry * 0.1,
        mega.x, mega.capY, Math.max(rx, ry)
      );
      capGrad.addColorStop(0, 'rgba(255, 230, 150, 0.85)');
      capGrad.addColorStop(0.2, 'rgba(240, 180, 80, 0.7)');
      capGrad.addColorStop(0.45, 'rgba(210, 140, 60, 0.55)');
      capGrad.addColorStop(0.7, 'rgba(170, 120, 80, 0.4)');
      capGrad.addColorStop(0.9, 'rgba(130, 100, 85, 0.2)');
      capGrad.addColorStop(1, 'rgba(100, 80, 70, 0)');

      ctx.fillStyle = capGrad;
      ctx.beginPath();
      ctx.ellipse(mega.x + wobble, mega.capY, rx, ry, 0, 0, Math.PI * 2);
      ctx.fill();

      // Inner bright core
      const innerGrad = ctx.createRadialGradient(
        mega.x, mega.capY - 5, 0,
        mega.x, mega.capY, ry * 0.5
      );
      innerGrad.addColorStop(0, 'rgba(255, 255, 230, 0.6)');
      innerGrad.addColorStop(0.4, 'rgba(255, 210, 130, 0.35)');
      innerGrad.addColorStop(1, 'rgba(255, 180, 90, 0)');
      ctx.fillStyle = innerGrad;
      ctx.beginPath();
      ctx.ellipse(mega.x + wobble * 0.5, mega.capY - 5, rx * 0.45, ry * 0.4, 0, 0, Math.PI * 2);
      ctx.fill();

      // Torus ring where cap meets stem
      if (mega.stemHeight > 30) {
        const ringY = mega.capY + ry * 0.55;
        const ringRx = rx * 0.65;
        const ringRy = ry * 0.2;
        ctx.fillStyle = 'rgba(190, 140, 80, 0.35)';
        ctx.beginPath();
        ctx.ellipse(mega.x + wobble * 0.3, ringY, ringRx, ringRy, 0, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  /**
   * Render bonus city award notification with fade-in/out animation.
   */
  _renderBonusCityNotification(ctx, w, h) {
    const notif = this.bonusCityNotification;
    if (!notif) return;

    const t = notif.timer;
    const dur = notif.duration;

    // Fade in during first 0.5s, stay full for 2s, fade out last 0.5s
    let alpha = 1;
    if (t < 0.5) {
      alpha = t / 0.5;
    } else if (t > dur - 0.5) {
      alpha = (dur - t) / 0.5;
    }
    alpha = Math.max(0, Math.min(1, alpha));

    ctx.save();
    ctx.globalAlpha = alpha;

    // Background panel (responsive)
    const hudScale = Math.min(1, w / 900);
    const panelW = Math.round(280 * hudScale + 40);
    const panelH = Math.round(44 * hudScale + 6);
    const panelX = w / 2 - panelW / 2;
    const panelY = h * 0.18;
    this._drawHUDPanel(ctx, panelX, panelY, panelW, panelH);

    // "BONUS CITY!" text with green glow
    const bonusFontSize = Math.max(14, Math.round(20 * hudScale));
    this.game.renderer.drawGlowText('BONUS CITY!', w / 2, panelY + panelH / 2 - 4, {
      font: `${bonusFontSize}px "Share Tech Mono", monospace`,
      color: '#00ff88',
      glowColor: '#00ff88',
      glowSize: Math.round(10 * hudScale),
    });

    // Score threshold text below
    const thresholdText = `${notif.threshold.toLocaleString()} PTS`;
    const threshFontSize = Math.max(9, Math.round(12 * hudScale));
    this.game.renderer.drawGlowText(thresholdText, w / 2, panelY + panelH / 2 + Math.round(14 * hudScale), {
      font: `${threshFontSize}px "Share Tech Mono", monospace`,
      color: '#aabbcc',
      glowColor: '#445566',
      glowSize: Math.round(3 * hudScale),
    });

    ctx.restore();
  }

  /**
   * Render background gradient that shifts color with wave progression.
   * Early waves: deep navy/black. Later waves: hints of dark purple/crimson.
   */
  _renderBackground(ctx, w, h) {
    const wave = this.wave;
    const gradient = ctx.createLinearGradient(0, 0, 0, h);

    // Wave-based color shifting
    // Wave 1: pure black top → deep navy bottom (#0a0a2e)
    // Waves 3-5: slight purple hue
    // Waves 6+: dark crimson/purple tones intensify
    const waveProgress = Math.min((wave - 1) / 12, 1.0); // 0 at wave 1, 1 at wave 13+

    // Top color: black → dark purple
    const topR = Math.floor(waveProgress * 15);
    const topG = 0;
    const topB = Math.floor(waveProgress * 10);

    // Bottom color: #0a0a2e → blend toward dark purple/crimson
    const botR = Math.floor(10 + waveProgress * 25);
    const botG = Math.floor(10 - waveProgress * 5);
    const botB = Math.floor(46 + waveProgress * 15);

    const topColor = `rgb(${topR}, ${topG}, ${topB})`;
    const botColor = `rgb(${botR}, ${Math.max(0, botG)}, ${botB})`;

    gradient.addColorStop(0, topColor);
    gradient.addColorStop(1, botColor);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);
  }

  /**
   * Update star positions (scrolling) - called from update()
   */
  _updateStarfield(dt) {
    for (const star of this.stars) {
      // Scroll: stars drift slowly downward
      star.y += star.speed * dt;
      if (star.y > 1.02) {
        star.y = -0.02;
        star.x = Math.random(); // reposition horizontally on wrap
      }

      // Twinkle: brightness oscillates
      const twinkle = 0.5 + 0.5 * Math.sin(this.starfieldTime * star.twinkleSpeed + star.twinklePhase);
      star.brightness = star.baseBrightness * (0.5 + 0.5 * twinkle);
    }
  }

  _renderStarfield(ctx, w, h) {
    ctx.save();
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;

    // Render small stars without glow (majority - fast path, no shadow)
    for (const star of this.stars) {
      if (star.size > 1.2) continue;
      ctx.globalAlpha = star.brightness;
      ctx.fillStyle = star.colorTint === 'blue' ? '#aaddff' :
                      star.colorTint === 'amber' ? '#ffeedd' : '#ffffff';
      ctx.beginPath();
      ctx.arc(star.x * w, star.y * h, star.size, 0, Math.PI * 2);
      ctx.fill();
    }

    // Render large stars with subtle glow (few stars only)
    for (const star of this.stars) {
      if (star.size <= 1.2) continue;
      ctx.globalAlpha = star.brightness;
      const starColor = star.colorTint === 'blue' ? '#aaddff' :
                        star.colorTint === 'amber' ? '#ffeedd' : '#ffffff';
      ctx.fillStyle = starColor;
      ctx.shadowColor = starColor;
      ctx.shadowBlur = star.size * 2;
      ctx.beginPath();
      ctx.arc(star.x * w, star.y * h, star.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  _renderCrosshair(ctx) {
    const x = this.crosshairX;
    const y = this.crosshairY;
    const size = 12;

    // Determine crosshair color: red flash when empty click, else cyan
    let crosshairColor = '#00ffff';
    let flashAlpha = 0;
    if (this.emptyClickFlash) {
      const t = this.emptyClickFlash.timer / this.emptyClickFlash.duration;
      flashAlpha = 1 - t; // fades out over duration
      crosshairColor = '#ff3333';
    }

    ctx.save();
    ctx.strokeStyle = crosshairColor;
    ctx.shadowColor = crosshairColor;
    ctx.shadowBlur = this.emptyClickFlash ? 10 : 6;
    ctx.lineWidth = this.emptyClickFlash ? 2.0 : 1.5;

    // Crosshair lines
    ctx.beginPath();
    ctx.moveTo(x - size, y);
    ctx.lineTo(x - size / 3, y);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x + size / 3, y);
    ctx.lineTo(x + size, y);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x, y - size);
    ctx.lineTo(x, y - size / 3);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x, y + size / 3);
    ctx.lineTo(x, y + size);
    ctx.stroke();

    // Center dot
    ctx.fillStyle = crosshairColor;
    ctx.beginPath();
    ctx.arc(x, y, 1.5, 0, Math.PI * 2);
    ctx.fill();

    // Empty click "X" indicator that fades out
    if (this.emptyClickFlash && flashAlpha > 0.1) {
      ctx.globalAlpha = flashAlpha * 0.8;
      ctx.strokeStyle = '#ff3333';
      ctx.shadowColor = '#ff3333';
      ctx.shadowBlur = 8;
      ctx.lineWidth = 2;
      const xSize = size * 0.6;
      ctx.beginPath();
      ctx.moveTo(x - xSize, y - xSize);
      ctx.lineTo(x + xSize, y + xSize);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x + xSize, y - xSize);
      ctx.lineTo(x - xSize, y + xSize);
      ctx.stroke();
    }

    ctx.restore();
  }

  _renderHUD(ctx, w, h) {
    ctx.save();

    // Responsive scaling factor for small screens
    const hudScale = Math.min(1, w / 900);
    const fontSize = (base) => Math.max(Math.round(base * 0.65), Math.round(base * hudScale));

    // --- Wave number panel (top-left) ---
    const wavePanelX = Math.round(8 * hudScale);
    const wavePanelY = Math.round(6 * hudScale);
    const wavePanelW = Math.round(110 * hudScale);
    const wavePanelH = Math.round(28 * hudScale);
    this._drawHUDPanel(ctx, wavePanelX, wavePanelY, wavePanelW, wavePanelH);
    this.game.renderer.drawGlowText(`WAVE ${this.wave}`, wavePanelX + wavePanelW / 2, wavePanelY + wavePanelH / 2, {
      font: `${fontSize(16)}px "Share Tech Mono", monospace`,
      color: '#00ff88',
      glowColor: '#00ff88',
      glowSize: Math.round(5 * hudScale),
    });

    // --- Score panel (top-center) ---
    const scoreFontSize = fontSize(18);
    const scoreText = `SCORE: ${this.score}`;
    ctx.font = `${scoreFontSize}px "Share Tech Mono", monospace`;
    const scoreMetrics = ctx.measureText(scoreText);
    const scorePanelW = Math.max(Math.round(140 * hudScale), scoreMetrics.width + Math.round(30 * hudScale));
    const scorePanelX = w / 2 - scorePanelW / 2;
    const scorePanelY = Math.round(6 * hudScale);
    const scorePanelH = Math.round(28 * hudScale);
    this._drawHUDPanel(ctx, scorePanelX, scorePanelY, scorePanelW, scorePanelH);
    this.game.renderer.drawGlowText(scoreText, w / 2, scorePanelY + scorePanelH / 2, {
      font: `${scoreFontSize}px "Share Tech Mono", monospace`,
      color: '#ffffff',
      glowColor: '#0088ff',
      glowSize: Math.round(5 * hudScale),
    });

    // --- Cities alive panel (top-right) ---
    const aliveCities = this.cities.filter(c => c.alive).length;
    const citiesPanelW = Math.round(110 * hudScale);
    const citiesPanelX = w - citiesPanelW - Math.round(8 * hudScale);
    const citiesPanelY = Math.round(6 * hudScale);
    const citiesPanelH = Math.round(28 * hudScale);
    this._drawHUDPanel(ctx, citiesPanelX, citiesPanelY, citiesPanelW, citiesPanelH);
    const citiesColor = aliveCities > 3 ? '#ffcc66' : aliveCities > 1 ? '#ffaa00' : '#ff3333';
    this.game.renderer.drawGlowText(`CITIES: ${aliveCities}`, citiesPanelX + citiesPanelW / 2, citiesPanelY + citiesPanelH / 2, {
      font: `${fontSize(14)}px "Share Tech Mono", monospace`,
      color: citiesColor,
      glowColor: citiesColor,
      glowSize: Math.round(4 * hudScale),
    });

    // --- Ammo counts near each battery ---
    for (let i = 0; i < this.batteries.length; i++) {
      const battery = this.batteries[i];
      this._renderBatteryAmmoHUD(ctx, battery, i);
    }

    ctx.restore();
  }

  /**
   * Draw a semi-transparent dark panel for HUD elements
   */
  _drawHUDPanel(ctx, x, y, w, h) {
    ctx.save();
    // Semi-transparent dark background
    ctx.fillStyle = 'rgba(0, 0, 20, 0.65)';
    ctx.beginPath();
    const r = 4; // border radius
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();

    // Subtle border glow
    ctx.strokeStyle = 'rgba(0, 136, 255, 0.25)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
  }

  /**
   * Render ammo count HUD near a battery position
   */
  _renderBatteryAmmoHUD(ctx, battery, index) {
    const w = this.game.width;
    const h = this.game.height;
    const hudScale = Math.min(1, w / 900);

    const ammoText = `${battery.ammo}`;
    const labelText = battery.alive ? ammoText : 'X';

    // Position the ammo display above the battery (scales with screen)
    const panelW = Math.round(36 * hudScale);
    const panelH = Math.round(20 * hudScale);
    const panelX = battery.x - panelW / 2;
    const panelY = battery.y - Math.round(45 * hudScale);

    // Draw semi-transparent panel
    this._drawHUDPanel(ctx, panelX, panelY, panelW, panelH);

    // Ammo count color: cyan if plenty, amber if low, red if empty/destroyed
    let color;
    if (!battery.alive) {
      color = '#ff3333';
    } else if (battery.ammo > 3) {
      color = '#00ffff';
    } else if (battery.ammo > 0) {
      color = '#ffaa00';
    } else {
      color = '#ff3333';
    }

    const ammoFontSize = Math.max(9, Math.round(13 * hudScale));
    this.game.renderer.drawGlowText(labelText, battery.x, panelY + panelH / 2, {
      font: `${ammoFontSize}px "Share Tech Mono", monospace`,
      color: color,
      glowColor: color,
      glowSize: Math.round(3 * hudScale),
    });
  }
}
